import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {MasterProductComplete, Product, ProductCategory} from '@kion/data-models';
import {ProductPriceService} from '../../services/product-price.service';
import {CookieService} from 'ngx-cookie-service';
import {TechnicalDetail} from './technical-data/technical-data.component';
import {Benefit} from './benefit-data/benefit-data.component';
import {BehaviorSubject, combineLatest, from, Observable, of} from 'rxjs';
import {FormBuilder, FormGroup} from '@angular/forms';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {Checkbox} from '../../../../../../shared/ui/src/lib/shared/elements/checkbox/checkbox.component';
import {TranslateService} from '@ngx-translate/core';
import {ProductsFacade} from '@kion/data-access/data-access-products';
import {map, take} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {CmsConfig} from '../../../../../cms/src/lib/config/cms.config';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {DropDownOption} from '../../../../../../shared/ui/src/lib/shared/elements/drop-down/drop-down.component';
import {DOCUMENT} from '@angular/common';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {PriceOnRequestDialogComponent} from '../../dialogs/price-on-request-dialog/price-on-request-dialog.component';
import {SubSink} from 'subsink';
import {ToastService} from '@kion/ui';

export interface Variant {
  code: string,
  name: string,
  price?: string,
  variantCodes: { code: string }[]
  isActive: boolean;
}

export interface VariantCategory {
  code: string,
  name: string,
  variants: Variant[]
}

@Component({
  selector: 'kion-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss']
})
export class ProductDetailComponent implements OnChanges, OnInit, OnDestroy {

  @Input() product: Product;
  @Input() masterProduct: MasterProductComplete;
  @Input() addingToBasket: boolean;

  @ViewChild('productInfo') productInfo: HTMLElement;

  // tslint:disable-next-line:no-output-rename
  @Output('addToBasket') addToBasketEmitter: EventEmitter<{ productId: string, quantity: number, options: { code: string }[] }> = new EventEmitter<{ productId: string, quantity: number, options: { code: string }[] }>();

  benefits$: Observable<Benefit[]>;
  benefitsFallback$: Observable<Benefit[]>;
  showBanner: boolean;

  form: FormGroup;

  subSink: SubSink;

  contractBox: Checkbox[] = [
    {
      title: this.translateService.instant('pdp.details.serviceContract.headline'),
      isLink: false,
      required: true,
      checked: false
    }
  ];

  vatSwitchOptions: DropDownOption[];

  icons: any;
  activeVariants$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  selectedProduct$: BehaviorSubject<Product> = new BehaviorSubject<Product>(null);
  private isContractSelected: boolean;
  private variantIds: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private variantProducts$: BehaviorSubject<Product[]> = new BehaviorSubject<Product[]>([]);
  private variants$: any;
  private window: Window;

  constructor(
    private priceService: ProductPriceService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private productsFacade: ProductsFacade,
    private router: Router,
    private cmsConfig: CmsConfig,
    private ngbModal: NgbModal,
    private toastService: ToastService,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
    formBuilder: FormBuilder
  ) {
    this.form = formBuilder.group({});
    this.window = this.document.defaultView;
    this.subSink = new SubSink();
  }

  get price(): string {
    if (this.cookieService.get('_bruttoPrices')) {
      return this.priceService.getBruttoPrice(this.product.price);
    }
    return this.priceService.getNettoPrice(this.product.price);
  }

  get strikedPrice(): string {
    if (this.cookieService.get('_bruttoPrices')) {
      return this.priceService.getBruttoStrikedPrice(this.product.price);
    }
    return this.priceService.getStrikedNettoPrice(this.product.price);
  }

  get isHeightEffected(): boolean {
    if (this.masterProduct && this.showBanner) {
      return !!this.masterProduct.masterProduct?.categories?.find(c => c.code === 'lift_height')
    }
    return false
  }

  get technicalDetails(): TechnicalDetail[] {
    if (this.product?.classifications) {
      const technicalDetails = this.product.classifications.find((classify) => classify.code === 'technicalDetails');
      if (technicalDetails) {
        return technicalDetails.features.map(
          (feature) => {
            return {
              key: feature.name,
              value: feature.formattedFeatureValue
            }
          }
        )
      }
      return [];
    }
    return [];
  }

  get isMasterButHasVariants() {
    if (this.masterProduct.variants.length > 0) {
      return this.product.id === this.masterProduct.masterProduct.id
    }
    return false;
  }

  get technicalDetailsFallback(): TechnicalDetail[] {
    if (this.masterProduct?.masterProduct?.classifications) {
      const technicalDetails = this.masterProduct.masterProduct.classifications.find((classify) => classify.code === 'technicalDetails');
      if (technicalDetails) {
        return technicalDetails.features.map(
          (feature) => {
            return {
              key: feature.name,
              value: feature.formattedFeatureValue
            }
          }
        )
      }
      return [];
    }
    return [];
  }

  get hasContract(): boolean {
    if (this.product) {
      return !!this.product?.productOptions?.find(po => po.code === 'CONTRACT')
    }
    return false;
  }

  get selectedVatSwitchTitle(): string {
    const selectedOption = this.vatSwitchOptions.find(option => option.selected);
    if (selectedOption.key === 'withoutVAT') {
      this.cookieService.delete('_bruttoPrices', '/');
    }
    if (selectedOption.key === 'withVAT') {
      this.cookieService.set('_bruttoPrices', 'true', 200, '/', null, true, "Strict");
    }
    return selectedOption.value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.masterProduct) {
      if (changes.masterProduct.previousValue !== changes.masterProduct.currentValue) {
        this.masterProduct = changes.masterProduct.currentValue;
        if (this.masterProduct.masterProduct?.classifications) {
          const benefits = this.masterProduct.masterProduct.classifications.find((classify) => classify.code === 'benefits');
          if (benefits) {
            this.benefitsFallback$ = of(benefits.features.map(
              (feature) => {
                return {
                  key: feature.name,
                  value: feature.formattedFeatureValue
                }
              }
            ))
          }
        }
        this.variantIds.next(this.masterProduct.masterProduct?.categories?.filter(c => c.isVariant).map(c => ({
          code: c.code,
          name: c.name
        })));
        this.variantProducts$.next(this.masterProduct.variants);
      }
    }
    if (changes.product) {
      this.product = changes.product.currentValue;
      this.selectedProduct$.next(this.product);
      const features = this.product.classifications?.find((classify) => classify.code === 'technicalDetails');
      this.isContractSelected = false;
      this.icons = features?.features.map(feature => {
        if (feature) {
          const code = feature.code.split('/1.0/technicalDetails.')[1];
          return {
            icon: './assets/icons/' + code + '_product_icon.svg',
            description: feature.formattedFeatureValue,
            alt: code + '_icon',
            title: code + '_icon',
          };
        }
      });
      if (this.product?.classifications) {
        const benefits = this.product.classifications.find((classify) => classify.code === 'benefits');
        if (benefits) {
          this.benefits$ = of(benefits.features.map(
            (feature) => {
              return {
                key: feature.name,
                value: feature.formattedFeatureValue
              }
            }
          ))
        }
      }
    }
  }

  ngOnInit(): void {
    this.showBanner = true;
    this.variants$ = combineLatest(
      [
        this.variantProducts$,
        this.variantIds,
        this.selectedProduct$
      ]
    ).pipe(
      map(
        ([products, variants, selected]) => {
          this.activeVariants$.next([]);
          const variantCategories = [];
          products.forEach(vp => {
            vp?.categories?.forEach(c => {
              variantCategories.push({...c, productId: vp.code})
            })
          });
          return variants?.map(
            (variant: { code: string, name: string }) => {
              return {
                code: variant.code,
                name: variant.name,
                variants: variantCategories.filter(category => category?.code?.startsWith(variant.code)).map(
                  (category: ProductCategory) => {
                    const codes = variantCategories.filter(c => c?.code?.startsWith(variant.code) && c.code === category.code).map(
                      c => {
                        return {
                          code: c.productId
                        }
                      }
                    );
                    return {
                      code: category.code,
                      name: category.name,
                      variantCodes: codes,
                      isActive: !!codes.find(c => c.code === selected.id)
                    }
                  }
                ).filter(
                  (category, index, self) => self.indexOf(self.find(c => c.code === category.code)) === index
                ).map(
                  category => {
                    if (category.isActive) {
                      category.variantCodes.forEach(
                        variantCode => {
                          this.activeVariants$.value.push(variantCode.code);
                        }
                      );
                    }
                    this.activeVariants$.next(this.activeVariants$.value);
                    return category;
                  }
                )
              }
            }
          );
        }
      )
    );
    let useCookie = false;
    const vatOption = this.route.snapshot.queryParams.price;
    if (!vatOption) {
      useCookie = true
    }
    this.vatSwitchOptions = [
      {
        key: 'withoutVAT',
        value: this.translateService.instant('pdp.details.vatSwitch.withoutVat'),
        selected: vatOption === "withoutVAT" || (useCookie && !this.cookieService.get('_bruttoPrices'))
      },
      {
        key: 'withVAT',
        value: this.translateService.instant('pdp.details.vatSwitch.withVat'),
        selected: vatOption === "withVAT" || (useCookie && !!this.cookieService.get('_bruttoPrices'))
      }
    ];
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  addToBasket(): void {
    const options = [];
    if (this.isContractSelected) {
      options.push({code: 'CONTRACT'});
    }
    this.addToBasketEmitter.emit(
      {
        productId: this.product.id,
        quantity: this.form.get('quantity').value,
        options
      }
    )
  }

  closeBanner(): void {
    this.showBanner = false;
  }

  contractSelected(selected: boolean) {
    this.isContractSelected = selected;
  }

  scrollToProductInfo(element: HTMLElement) {
    element.scrollIntoView({behavior: "smooth"});
  }

  selectVariant(variantCodes: any) {
    const currentFragment = this.product.code;
    let variants: VariantCategory[] = [];
    this.variants$.pipe(take(1)).subscribe(
      v => variants = v
    );
    if (Array.isArray(variantCodes) && variantCodes.length > 0) {
      const selectedVariants = variantCodes.map(v => v.code).filter(v => v !== currentFragment).sort();
      const productId = this.getNewProductSelection(selectedVariants, variants, currentFragment);
      const x = window.pageXOffset;
      const y = window.pageYOffset;
      this.router.navigate([
        this.cmsConfig.routes?.productBaseRoute,
        this.masterProduct.masterProduct.id,
        this.masterProduct.masterProduct.urlName,
      ], {queryParams: {variant: productId}}).then(() => {
      });
    }
  }

  selectVatSwitchOption(option: DropDownOption): void {
    this.vatSwitchOptions = this.vatSwitchOptions.map(
      (vatSwitchOption: DropDownOption) => {
        return {
          ...vatSwitchOption,
          selected: vatSwitchOption === option
        }
      }
    );
  }

  priceOnRequestCall() {
    const modalRef = this.ngbModal.open(PriceOnRequestDialogComponent);
    modalRef.componentInstance.productCode = this.product.id;
    modalRef.componentInstance.productTitle = this.product.name;
    this.subSink.sink = from(modalRef.result).subscribe(
      result => {
        if (result.send) {
          this.toastService.show(
            {
              type: 'success',
              text: this.translateService.instant('dialog.priceOnRequest.toast.submit.success')
            }
          )
        }
      }
    )
  }

  private getNewProductSelection(selectedVariants: Array<string>, currentVariants: VariantCategory[], currentFragment: string) {
    if (selectedVariants.length === 1) {
      return selectedVariants[0];
    }
    const codes = [];
    currentVariants.map((variants: VariantCategory) => {
      const vars = variants.variants.filter(v => v.isActive);
      if (vars[0]) {
        vars[0].variantCodes.map(v => v.code).filter(v => v !== currentFragment).forEach(v => codes.push(v));
      }
    });

    function selectedDecVariantSort(a, b) {
      if (a.length > b.length) {
        return -1;
      }
      if (a.length < b.length) {
        return 1;
      }
      return 0;
    }

    const newS = codes.reduce((countMap, word) => {
      countMap[word] = ++countMap[word] || 1;
      return countMap
    }, {});
    const currentSelection = Object.keys(newS).map((k, idx) => ({
        code: k,
        length: newS[k]
      })
    )
      .filter(cS => selectedVariants.indexOf(cS.code) > -1)
      .sort(selectedDecVariantSort);

    return currentSelection[0].code || selectedVariants[0];
  }
}
